import React from 'react';
import { motion } from 'framer-motion';
import {
  Brain,
  ShieldCheck,
  TrendingUp,
  BarChart as ChartBar,
  DollarSign,
  Zap,
} from 'lucide-react';

export function Hero() {
  return (
    <div className="relative overflow-hidden bg-dark-card border-b border-dark-border">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-600/10 via-transparent to-transparent" />

      <div className="mx-auto max-w-7xl relative">
        <div className="relative z-10 pb-8 sm:pb-16 md:pb-20 lg:w-full lg:max-w-2xl lg:pb-28 xl:pb-32">
          <main className="mx-auto mt-10 max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="sm:text-center lg:text-left"
            >
              {/* Main Headline */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex items-center justify-center lg:justify-start mb-6 space-x-2"
              >
                <span className="px-3 py-1 text-sm font-semibold text-primary-400 bg-primary-400/10 rounded-full">
                  #1 in Financial AI
                </span>
                <span className="text-dark-muted">|</span>
                <span className="text-dark-muted">Trusted by 10,000+ businesses</span>
              </motion.div>

              <h1 className="text-4xl font-bold tracking-tight text-dark-text sm:text-5xl md:text-6xl">
                <span className="block">Supercharge Your</span>
                <span className="block text-primary-400">Financial Future with AI</span>
              </h1>

              {/* Value Proposition */}
              <p className="mt-3 text-base text-dark-muted sm:mx-auto sm:mt-5 sm:max-w-xl sm:text-lg md:mt-5 md:text-xl lg:mx-0">
                Join thousands of businesses using CashFlow AI to unlock growth, predict
                opportunities, and make smarter financial decisions in real-time. Your intelligent
                financial co-pilot is here.
              </p>

              {/* Stats Section */}
              <div className="mt-8 grid grid-cols-3 gap-4 sm:gap-6">
                <div className="text-center lg:text-left">
                  <div className="text-2xl font-bold text-primary-400">98%</div>
                  <div className="text-sm text-dark-muted">Accuracy Rate</div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-2xl font-bold text-primary-400">2.5x</div>
                  <div className="text-sm text-dark-muted">Faster Processing</div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-2xl font-bold text-primary-400">30%</div>
                  <div className="text-sm text-dark-muted">Cost Reduction</div>
                </div>
              </div>

              {/* CTA Button */}
              <div className="mt-8 sm:mt-10 sm:flex sm:justify-center lg:justify-start">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="rounded-md shadow"
                >
                  <a
                    href="#pricing"
                    className="flex w-full items-center justify-center rounded-md bg-primary-600 px-8 py-3 text-base font-medium text-dark-text hover:bg-primary-700 md:py-4 md:px-10 md:text-lg"
                  >
                    Start 7-Day Free Trial
                  </a>
                </motion.div>
              </div>

              {/* Features Grid */}
              <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-3">
                <Feature
                  icon={<Brain className="h-6 w-6" />}
                  title="AI-Powered Insights"
                  description="Get intelligent cash flow forecasting and real-time financial insights"
                />
                <Feature
                  icon={<ShieldCheck className="h-6 w-6" />}
                  title="Bank-Grade Security"
                  description="Your financial data is protected with enterprise-level encryption"
                />
                <Feature
                  icon={<TrendingUp className="h-6 w-6" />}
                  title="Real-Time Analytics"
                  description="Monitor your cash flow with live dashboards and alerts"
                />
              </div>

              {/* Case Study Preview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-16 bg-dark-bg rounded-lg p-6 border border-dark-border"
              >
                <div className="flex items-center space-x-4">
                  <img
                    src="https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=100&h=100&fit=crop&q=80"
                    alt="Company Logo"
                    className="w-16 h-16 rounded-full"
                  />
                  <div>
                    <h3 className="text-lg font-semibold text-dark-text">Tech Solutions Inc.</h3>
                    <p className="text-primary-400">200% ROI in 6 months</p>
                  </div>
                </div>
                <blockquote className="mt-4 text-dark-muted">
                  "CashFlow AI transformed our financial operations. We've reduced processing time
                  by 75% and improved accuracy significantly."
                </blockquote>
                <p className="mt-2 text-sm text-dark-text">- Sarah Chen, CFO</p>
              </motion.div>
            </motion.div>
          </main>
        </div>
      </div>

      {/* Right Side Image */}
      <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
        <img
          className="h-56 w-full object-cover sm:h-72 md:h-96 lg:h-full lg:w-full opacity-80"
          src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2015&q=80"
          alt="Financial dashboard and analytics"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-dark-bg/50 via-transparent to-transparent lg:block" />
      </div>
    </div>
  );
}

function Feature({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
      className="flex items-start space-x-4"
    >
      <div className="flex-shrink-0">
        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-600/20 text-primary-400">
          {icon}
        </div>
      </div>
      <div>
        <h3 className="text-lg font-medium text-dark-text">{title}</h3>
        <p className="mt-2 text-base text-dark-muted">{description}</p>
      </div>
    </motion.div>
  );
}
